import { UAContext } from "@quentin-sommer/react-useragent";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X } from "lucide-react";
import { CSSProperties, ReactNode, useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Sheet } from "react-modal-sheet";
import cn from "~/utils/cn";

interface ModalProps {
  onClose: () => void;
  children: ReactNode;
  isOpen: boolean;
  closeButton?: boolean; // whether to show the close button, default is true
  label?: string; // accessible name of the dialog
  classNames?: {
    mask?: string;
    dialog?: string;
    content?: string;
  };
  styles?: {
    mask?: CSSProperties;
    dialog?: CSSProperties;
  };
}

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

// Tab and Shift+Tab wrap around inside the open dialog panel.
function trapTab(e: KeyboardEvent, panel: HTMLElement | null) {
  if (e.key !== "Tab" || !panel) return;
  const items = panel.querySelectorAll<HTMLElement>(FOCUSABLE);
  if (!items.length) return;
  const first = items[0];
  const last = items[items.length - 1];
  if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

const Modal = ({
  onClose,
  children,
  isOpen,
  closeButton = true,
  label,
  classNames = { mask: "", dialog: "", content: "" },
  styles = { mask: {}, dialog: {} },
}: ModalProps) => {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const panel = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;

  // While open: focus moves into the dialog, Tab stays inside, Escape closes, and focus returns to the opener.
  useEffect(() => {
    if (!isOpen) return;
    const opener = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") return close.current();
      trapTab(e, panel.current);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      opener?.focus?.();
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={styles.mask}
            onClick={onClose}
            aria-hidden
            className={cn("fixed inset-0 z-overlay bg-scrim backdrop-blur-sm", classNames.mask)}
          />
          <div className="pointer-events-none fixed inset-0 z-overlay grid place-items-center p-[var(--gutter)]">
            <motion.div
              ref={panel}
              role="dialog"
              aria-modal="true"
              aria-label={label}
              tabIndex={-1}
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={reduce ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.98, transition: { duration: 0.15 } }}
              transition={{ type: "spring", stiffness: 420, damping: 32 }}
              style={styles.dialog}
              className={cn(
                "pointer-events-auto relative w-full max-w-md rounded-lg border border-line bg-raised text-ink shadow-sheet outline-none",
                classNames.dialog,
              )}
            >
              {closeButton && (
                <button
                  type="button"
                  onClick={onClose}
                  aria-label={t("ui.profile.modal.close")}
                  className="absolute end-3 top-3 z-10 grid size-11 place-items-center rounded-full text-ink-2 transition-colors hover:bg-accent-soft hover:text-ink active:scale-95"
                >
                  <X className="size-5" strokeWidth={1.75} />
                </button>
              )}
              <div className={cn("max-h-[calc(100dvh_-_2_*_var(--gutter))] overflow-y-auto overscroll-contain px-6 pb-6 pt-8", classNames.content)}>{children}</div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

// Phones get a drag-to-dismiss bottom sheet, everything else the centered dialog above.
export function SheetOrModal({ visible, onClose, label, children }: { visible: boolean; onClose: () => void; label?: string; children: ReactNode }) {
  const { uaResults } = useContext(UAContext);
  const close = useRef(onClose);
  close.current = onClose;
  const sheet = useRef<HTMLDivElement>(null);
  // The sheet has no keyboard handling of its own: move focus in once it is visible, keep Tab inside,
  // let Escape close it, and hand focus back to the opener on close (same as Modal).
  useEffect(() => {
    if (!visible || !uaResults.mobile) return;
    const opener = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => (e.key === "Escape" ? close.current() : trapTab(e, sheet.current));
    const focus = setTimeout(() => sheet.current?.focus({ preventScroll: true }), 80);
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(focus);
      document.removeEventListener("keydown", onKey);
      opener?.focus?.({ preventScroll: true });
    };
  }, [visible, uaResults.mobile]);

  if (uaResults.mobile)
    return (
      <Sheet isOpen={visible} onClose={onClose} detent="content-height" tweenConfig={{ ease: [0.16, 1, 0.3, 1], duration: 0.45 }} style={{ zIndex: 100 }}>
        <Sheet.Container className="!rounded-t-lg !bg-raised !text-ink !shadow-sheet">
          <Sheet.Header>
            <span className="mx-auto mb-1 mt-2.5 block h-1.5 w-10 rounded-full bg-line-2" aria-hidden />
          </Sheet.Header>
          <Sheet.Scroller>
            <Sheet.Content>
              <div
                ref={sheet}
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-label={label}
                className="px-[var(--gutter)] pb-[calc(env(safe-area-inset-bottom)+1.5rem)] pt-3 outline-none"
              >
                {children}
              </div>
            </Sheet.Content>
          </Sheet.Scroller>
        </Sheet.Container>
        <Sheet.Backdrop className="!bg-scrim backdrop-blur-sm" onTap={onClose} tabIndex={-1} aria-hidden />
      </Sheet>
    );

  return (
    <Modal isOpen={visible} onClose={onClose} label={label}>
      {children}
    </Modal>
  );
}

export default Modal;
